import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { CloudinaryModule } from '@cloudinary/ng';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { CLOUDINARY_SERVICE } from '@smsamiinc/catalog';
import { BRANDING_SERVICE, LOCALE_SERVICE, PHONE_NUMBER_SERVICE, TRANSLATION_SERVICE } from '@smsamiinc/invoice';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';

import { environment } from '../environments/environment';

import { ActiveService } from './active-service';
import { AppProgressBarService } from './app-progressbar.service';
import { TokenHttpinterceptor } from './app.interceptor';
import { routes } from './app.routes';
import { AuthState, AuthService } from './auth/store';
import { OrgState, OrgService } from './dashboard/stores/org';
import { GoogleAnalytiscService } from './google-Analytics.service';
import { AuthGuard } from './guards/auth.guard';
import { ConfirmationGuard } from './guards/confirmation/confirmation.guard';
import { TranslocoHttpLoader } from './i18n/transloco-loader';
import { NotificationState, NotificationService } from './notification/store';
import { MaterialBrandingService } from './services/branding/material-branding.service';
import { CloudinaryService } from './services/cloudinary/cloudinary.service';
import { LocaleService } from './services/locale/locale.service';
import { PhoneNumberService } from './services/phone-number/phone-number.service';

/**
 * Application configuration for standalone Angular architecture
 *
 * NOTE: SalesTaxReporterService moved to dashboard route-level providers
 * since it's only used for POS receipt printing functionality
 */
export const appConfig: ApplicationConfig = {
    providers: [
        // Routing with component input binding
        provideRouter(routes, withComponentInputBinding()),

        // HTTP Client with legacy interceptor support
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenHttpinterceptor,
            multi: true,
        },

        // Browser animations
        provideAnimations(),

        // Material configuration
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'en-GB',
        },

        // NGXS State Management (root-level state only)
        // AuthState must be global because AuthGuard and storage plugin depend on it
        // OrgState must be global because DashboardRestaurantGuard depends on it
        // Feature states (Dashboard, etc.) are provided at route level
        importProvidersFrom(
            NgxsModule.forRoot([NotificationState, AuthState, OrgState]),
            NgxsStoragePluginModule.forRoot({
                keys: ['auth.access_token', 'auth.pinToken'],
            }),
            NgxsFormPluginModule.forRoot(),
            NgxsRouterPluginModule.forRoot(),
            NgxsReduxDevtoolsPluginModule.forRoot()
        ),

        // Third-party modules that don't have standalone providers yet
        importProvidersFrom(CloudinaryModule, InfiniteScrollModule),

        // Transloco i18n configuration
        provideTransloco({
            config: {
                availableLangs: [
                    { id: 'en', label: 'English' },
                    { id: 'es', label: 'Español' },
                    { id: 'ur', label: 'اردو' },
                    { id: 'ar', label: 'العربية' },
                    { id: 'ps', label: 'پښتو' },
                    { id: 'bn', label: 'বাংলা' },
                    { id: 'hi', label: 'हिन्दी' },
                ],
                defaultLang: 'en',
                reRenderOnLangChange: true,
                prodMode: environment.production,
            },
            loader: TranslocoHttpLoader,
        }),

        // Service Worker (production only)
        provideServiceWorker('ngsw-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerImmediately',
        }),

        // Cross-cutting service providers (used by plugins)
        {
            provide: TRANSLATION_SERVICE,
            useExisting: TranslocoService,
        },
        {
            provide: BRANDING_SERVICE,
            useClass: MaterialBrandingService,
        },
        {
            provide: LOCALE_SERVICE,
            useClass: LocaleService,
        },
        {
            provide: PHONE_NUMBER_SERVICE,
            useClass: PhoneNumberService,
        },
        {
            provide: CLOUDINARY_SERVICE,
            useClass: CloudinaryService,
        },

        // Global services (available throughout the app)
        AuthGuard,
        ConfirmationGuard,
        AuthService,
        OrgService,
        ActiveService,
        AppProgressBarService,
        GoogleAnalytiscService,
        NotificationService,
        // NOTE: SalesTaxReporterService removed - now provided at dashboard route level
    ],
};
